/**
 * Gift cards view component.
 * @module components/View/GiftCardsView
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Input,
  Label,
  Table,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  TableHeaderCell,
} from 'semantic-ui-react';
import { Link, useHistory } from 'react-router-dom';

import { Icon } from '@plone/volto/components';
import { getVocabulary } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';

import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';

const formatDate = (date) => {
  if (!date) return '';
  const parsed = new Date(date);
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  let hours = parsed.getHours();
  const minutes = parsed.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
  return `${year}-${month}-${day} ${hours}:${minutesStr} ${ampm}`;
};

/**
 * Gift cards view component class.
 * @function GiftCardsView
 * @param {Object} content Content object.
 * @returns {string} Markup of the component.
 */
const GiftCardsView = ({ content }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getVocabulary({ vocabNameOrURL: 'contacts' }));
    dispatch(getVocabulary({ vocabNameOrURL: 'giftcard_status' }));
  }, [dispatch]);

  const contactsVocabulary = useSelector(
    (state) => state.vocabularies?.contacts?.items || [],
  );
  const statusVocabulary = useSelector(
    (state) => state.vocabularies?.giftcard_status?.items || [],
  );

  const contactTitle = (value) =>
    contactsVocabulary.find((item) => item.value === value)?.label || value;
  const statusTitle = (value) =>
    statusVocabulary.find((item) => item.value === value)?.label || value;
  const statusClass = (status) =>
    status === 'redeemed' ? 'status-paid' : 'status-open';

  let items = content.items.map((item) => ({
    ...item,
    contact: contactTitle(item.contact),
    statusValue: item.status,
    status: statusTitle(item.status),
  }));

  // Filtering
  const [code, setCode] = useState('');
  const [contact, setContact] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTill, setDateTill] = useState('');

  const codeRef = useRef(null);
  const contactRef = useRef(null);

  const filterField = (items, field, value) =>
    value === ''
      ? items
      : (items = items.filter(
          (item) =>
            item[field] &&
            item[field].toLowerCase().indexOf(value.toLowerCase()) !== -1,
        ));

  const filterDateRange = (items, from, till) => {
    let filtered = items;
    if (from) {
      const fromTime = new Date(from).getTime();
      filtered = filtered.filter(
        (item) => item.date && new Date(item.date).getTime() >= fromTime,
      );
    }
    if (till) {
      // "Till" is a date with no time, so make it inclusive of the whole
      // day rather than cutting off at its midnight.
      const tillTime = new Date(till).getTime() + 24 * 60 * 60 * 1000 - 1;
      filtered = filtered.filter(
        (item) => item.date && new Date(item.date).getTime() <= tillTime,
      );
    }
    return filtered;
  };

  items = filterField(items, 'code', code);
  items = filterField(items, 'contact', contact);
  items = filterDateRange(items, dateFrom, dateTill);

  // Sorting
  const [sort, setSort] = useState('code');
  const [dir, setDir] = useState('asc');

  items = items.sort((a, b) =>
    (dir === 'asc' ? a[sort] > b[sort] : a[sort] < b[sort]) ? 1 : -1,
  );

  const onSort = (value) => {
    if (sort === value) {
      setDir(dir === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(value);
      setDir('asc');
    }
  };

  const SortIcon = () => (
    <Icon
      name={dir === 'desc' ? upSVG : downSVG}
      className="icon"
      size="20px"
    />
  );

  return (
    <article id="gift-cards">
      <div className="button-toolbar">
        <Link
          className="circular-button primary float-left"
          to={`${flattenToAppURL(content['@id'])}/add?type=Giftcard`}
        >
          <Icon name={addSVG} size="24px" />
        </Link>
        <div className="button-toolbar-title">
          Gift Cards <Label circular>{items.length}</Label>
        </div>
      </div>
      <div className="view-padding">
        <div className="scrollable-table">
          <Table striped celled selectable>
            <TableHeader>
              <TableRow className="tools-header">
                <TableHeaderCell onClick={() => onSort('code')} width="3">
                  Code {sort === 'code' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('contact')} width="4">
                  Contact {sort === 'contact' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('date')} width="3">
                  Date {sort === 'date' && <SortIcon />}
                </TableHeaderCell>
                <TableHeaderCell onClick={() => onSort('status')} width="2">
                  Status {sort === 'status' && <SortIcon />}
                </TableHeaderCell>
              </TableRow>
              <TableRow className="tools-filter">
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={code}
                      placeholder="Filter code"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={codeRef}
                      onChange={(e) => setCode(e.target.value.toUpperCase())}
                    />
                    {code && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setCode('');
                          codeRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-input">
                    <Input
                      value={contact}
                      placeholder="Filter contact"
                      autoCorrect="off"
                      spellCheck="false"
                      ref={contactRef}
                      onChange={(e) => setContact(e.target.value)}
                    />
                    {contact && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setContact('');
                          contactRef.current?.focus();
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell>
                  <div className="filter-date-range">
                    <input
                      type="date"
                      aria-label="From"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                    <input
                      type="date"
                      aria-label="Till"
                      value={dateTill}
                      onChange={(e) => setDateTill(e.target.value)}
                    />
                    {(dateFrom || dateTill) && (
                      <Icon
                        name={clearSVG}
                        size="16px"
                        className="clear-icon"
                        onClick={() => {
                          setDateFrom('');
                          setDateTill('');
                        }}
                      />
                    )}
                  </div>
                </TableHeaderCell>
                <TableHeaderCell />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow
                  key={item['@id']}
                  className="clickable-row"
                  onClick={() => history.push(flattenToAppURL(item['@id']))}
                >
                  <TableCell>{item.code}</TableCell>
                  <TableCell>{item.contact}</TableCell>
                  <TableCell>{formatDate(item.date)}</TableCell>
                  <TableCell className={statusClass(item.statusValue)}>
                    {item.status}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </article>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
GiftCardsView.propTypes = {
  content: PropTypes.shape({
    items: PropTypes.array,
  }).isRequired,
};

export default GiftCardsView;
